import axiosClient from '../../../api/axiosClient';
import { CalendarEvent, allCalendarEvents } from '../../../pages/Calendar/calendarMockData';

// Store for mock events (simulating in-memory database)
let mockEvents: CalendarEvent[] = [...allCalendarEvents];

/**
 * Calendar Event Service
 * Handles all calendar event CRUD operations with fallback to mock data
 */

/**
 * Get all calendar events
 */
export const getCalendarEvents = async (): Promise<CalendarEvent[]> => {
  try {
    const response = await axiosClient.get<CalendarEvent[]>('/calendar/events');
    return response.data;
  } catch (error) {
    console.warn('Failed to fetch calendar events from API, using mock data:', error);
    return mockEvents;
  }
};

/**
 * Get calendar event by ID
 */
export const getCalendarEvent = async (id: string): Promise<CalendarEvent | null> => {
  try {
    const response = await axiosClient.get<CalendarEvent>(`/calendar/events/${id}`);
    return response.data;
  } catch (error) {
    console.warn(`Failed to fetch calendar event ${id} from API, using mock data:`, error);
    const event = mockEvents.find((e) => e.id === id);
    return event || null;
  }
};

/**
 * Create a new calendar event
 */
export const createCalendarEvent = async (
  eventData: Omit<CalendarEvent, 'id'>
): Promise<CalendarEvent> => {
  try {
    const response = await axiosClient.post<CalendarEvent>('/calendar/events', eventData);
    return response.data;
  } catch (error) {
    console.warn('Failed to create calendar event via API, using mock data:', error);

    // Generate new event with mock ID
    const newEvent: CalendarEvent = {
      ...eventData,
      id: `event-${Date.now()}`,
    };

    mockEvents.push(newEvent);
    return newEvent;
  }
};

/**
 * Update an existing calendar event
 */
export const updateCalendarEvent = async (
  id: string,
  updates: Partial<CalendarEvent>
): Promise<CalendarEvent> => {
  try {
    const response = await axiosClient.put<CalendarEvent>(`/calendar/events/${id}`, updates);
    return response.data;
  } catch (error) {
    console.warn(`Failed to update calendar event ${id} via API, using mock data:`, error);

    // Update in mock data
    const eventIndex = mockEvents.findIndex((e) => e.id === id);

    if (eventIndex === -1) {
      throw new Error(`Calendar event with id ${id} not found`);
    }

    const updatedEvent = {
      ...mockEvents[eventIndex],
      ...updates,
      id, // Ensure ID doesn't change
    };

    mockEvents[eventIndex] = updatedEvent;
    return updatedEvent;
  }
};

/**
 * Delete a calendar event
 */
export const deleteCalendarEvent = async (id: string): Promise<void> => {
  try {
    await axiosClient.delete(`/calendar/events/${id}`);
  } catch (error) {
    console.warn(`Failed to delete calendar event ${id} via API, using mock data:`, error);

    // Delete from mock data
    const eventIndex = mockEvents.findIndex((e) => e.id === id);

    if (eventIndex === -1) {
      throw new Error(`Calendar event with id ${id} not found`);
    }

    mockEvents.splice(eventIndex, 1);
  }
};

/**
 * Get events by date range
 */
export const getEventsByDateRange = async (
  startDate: Date,
  endDate: Date
): Promise<CalendarEvent[]> => {
  try {
    const response = await axiosClient.get<CalendarEvent[]>('/calendar/events/range', {
      params: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
    });
    return response.data;
  } catch (error) {
    console.warn('Failed to fetch events by date range from API, using mock data:', error);

    // Filter mock events by date range
    return mockEvents.filter((event) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      return eventStart <= endDate && eventEnd >= startDate;
    });
  }
};

/**
 * Get events by type
 */
export const getEventsByType = async (
  type: CalendarEvent['type']
): Promise<CalendarEvent[]> => {
  try {
    const response = await axiosClient.get<CalendarEvent[]>(`/calendar/events/type/${type}`);
    return response.data;
  } catch (error) {
    console.warn(`Failed to fetch events by type ${type} from API, using mock data:`, error);
    return mockEvents.filter((e) => e.type === type);
  }
};

/**
 * Get the current mock events (for development/testing)
 */
export const getMockEvents = (): CalendarEvent[] => {
  return [...mockEvents];
};

/**
 * Reset mock events to initial state (for development/testing)
 */
export const resetMockEvents = (): void => {
  mockEvents = [...allCalendarEvents];
};
